import { useState, useEffect, useCallback, useRef } from "react";
import { router } from "@inertiajs/react";
import { motion, useAnimationControls, AnimatePresence } from "framer-motion";
import Win2kWindow from "@/components/win2k/Win2kWindow";
import Win2kButton from "@/components/win2k/Win2kButton";
import { Win2kMenu, Win2kMenuItem } from "@/components/win2k/Win2kMenu";
import KickPlayerPicker from "@/components/win2k/KickPlayerPicker";
import KickConfirmDialog from "@/components/win2k/KickConfirmDialog";
import RulesModal from "@/components/win2k/RulesModal";
import FeltTable from "@/components/game/FeltTable";
import PlayerArea, { type PlayerData } from "@/components/game/PlayerArea";
import DeckPile from "@/components/game/DeckPile";
import GameControls from "@/components/game/GameControls";
import ForceSkipDialog from "@/components/game/ForceSkipDialog";
import Scoreboard from "@/components/game/Scoreboard";
import RoundEndSummary, { type RoundResults } from "@/components/game/RoundEndSummary";
import TargetPicker from "@/components/game/TargetPicker";
import GameOverScreen from "@/components/game/GameOverScreen";
import ActionCardAlert from "@/components/game/ActionCardAlert";
import { DeckPositionProvider } from "@/components/game/DeckPositionContext";
import consumer from "@/lib/cable";
import { useTurnNotification } from "@/hooks/useTurnNotification";
import { useActionCardNotification } from "@/hooks/useActionCardNotification";
import { useAutoplay } from "@/hooks/useAutoplay";

interface Player {
  username: string;
  is_admin: boolean;
  connected: boolean;
}

interface PendingAction {
  type: "action";
  value: number | null;
  subtype: string | null;
}

interface ActionEvent {
  action: string;
  attacker: string;
  target: string;
}

interface GameStateData {
  round: number;
  turn_index: number;
  current_player: string;
  turn_state: string;
  flip_three_remaining: number;
  player_order: string[];
  players: PlayerData[];
  deck: { remaining: number };
  scores: Record<string, number>;
  score_limit: number;
  finished: boolean;
  winners: string[];
  pending_action: PendingAction | null;
  pending_held_actions_count: number;
  last_round_results: RoundResults | null;
  last_action_event: ActionEvent | null;
}

interface RoomShowProps {
  room: {
    code: string;
    status: string;
  };
  players: Player[];
  current_player: {
    username: string;
    is_admin: boolean;
  };
}

type Subscription = ReturnType<typeof consumer.subscriptions.create>;

export default function RoomShow({
  room,
  players: initialPlayers,
  current_player,
}: RoomShowProps) {
  const [roomStatus, setRoomStatus] = useState(room.status);
  const [gameState, setGameState] = useState<GameStateData | null>(null);
  const [lobbyPlayers, setLobbyPlayers] = useState<Player[] | null>(null);
  const subscriptionRef = useRef<Subscription | null>(null);
  const onMessageRef = useRef<(data: Record<string, unknown>) => void>(() => {});

  useEffect(() => {
    const subscription = consumer.subscriptions.create(
      { channel: "GameChannel", room_code: room.code },
      {
        received: (data: Record<string, unknown>) => {
          if ((data.type as string) === "kicked") {
            subscription.unsubscribe();
            router.visit("/?kicked=true");
            return;
          }
          onMessageRef.current(data);
        },
      }
    );
    subscriptionRef.current = subscription;
    return () => subscription.unsubscribe();
  }, [room.code]);

  const sendAction = useCallback(
    (gameAction: string, extra?: Record<string, unknown>) => {
      subscriptionRef.current?.send({ game_action: gameAction, ...extra });
    },
    []
  );

  const handleGameStart = useCallback((gs: GameStateData) => {
    setRoomStatus("playing");
    setGameState(gs);
  }, []);

  const handleReturnToLobby = useCallback((players: Player[]) => {
    setLobbyPlayers(players);
    setGameState(null);
    setRoomStatus("waiting");
  }, []);

  const handleLeaveRoom = useCallback(() => {
    subscriptionRef.current?.send({ game_action: "leave_room" });
    subscriptionRef.current?.unsubscribe();
    router.visit("/");
  }, []);

  if (roomStatus === "waiting") {
    return (
      <Lobby
        room={room}
        initialPlayers={lobbyPlayers ?? initialPlayers}
        currentPlayer={current_player}
        onGameStart={handleGameStart}
        onMessageRef={onMessageRef}
        sendAction={sendAction}
        onLeaveRoom={handleLeaveRoom}
      />
    );
  }

  return (
    <GameView
      room={room}
      currentPlayer={current_player}
      initialGameState={gameState}
      onReturnToLobby={handleReturnToLobby}
      onMessageRef={onMessageRef}
      sendAction={sendAction}
    />
  );
}

function GameView({
  room,
  currentPlayer,
  initialGameState,
  onReturnToLobby,
  onMessageRef,
  sendAction,
}: {
  room: RoomShowProps["room"];
  currentPlayer: RoomShowProps["current_player"];
  initialGameState: GameStateData | null;
  onReturnToLobby: (players: Player[]) => void;
  onMessageRef: React.MutableRefObject<(data: Record<string, unknown>) => void>;
  sendAction: (action: string, extra?: Record<string, unknown>) => void;
}) {
  const [gameState, setGameState] = useState<GameStateData | null>(initialGameState);
  const [roundSummary, setRoundSummary] = useState<RoundResults | null>(null);
  const [showGameOver, setShowGameOver] = useState(false);
  const [showKickPicker, setShowKickPicker] = useState(false);
  const [kickTarget, setKickTarget] = useState<string | null>(null);
  const [showForceSkipDialog, setShowForceSkipDialog] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const onReturnToLobbyRef = useRef(onReturnToLobby);
  onReturnToLobbyRef.current = onReturnToLobby;
  const dismissedRoundRef = useRef<number | null>(null);

  useEffect(() => {
    onMessageRef.current = (data: Record<string, unknown>) => {
      const type = data.type as string;
      if (type === "game_state") {
        const state = data.state as GameStateData;
        if (state.last_round_results && state.last_round_results.round !== dismissedRoundRef.current) {
          setRoundSummary(state.last_round_results);
        } else if (state.finished) {
          setShowGameOver(true);
        }
        setGameState(state);
      } else if (type === "return_to_lobby") {
        onReturnToLobbyRef.current(data.players as Player[]);
      }
    };
  }, [onMessageRef]);

  const sendSelectTarget = useCallback(
    (target: string) => {
      sendAction("select_target", { target });
    },
    [sendAction]
  );

  function handleKickConfirm() {
    if (kickTarget) {
      sendAction("kick_player", { target: kickTarget });
    }
    setKickTarget(null);
  }

  const isMyTurn = gameState?.current_player === currentPlayer.username;
  useTurnNotification(isMyTurn);

  const shakeControls = useAnimationControls();
  const {
    suspenseAttacker,
    pendingCardType,
    alertData,
    flashColor,
    dismissAlert,
  } = useActionCardNotification({
    gameState,
    currentUsername: currentPlayer.username,
    shakeControls,
  });

  const myAutoplayData = gameState?.players.find(
    (p) => p.username === currentPlayer.username
  );
  useAutoplay({
    isMyTurn,
    turnState: gameState?.turn_state ?? "",
    myPlayerData: myAutoplayData,
    flipThreeRemaining: gameState?.flip_three_remaining ?? 0,
    pendingAction: gameState?.pending_action ?? null,
    players: gameState?.players ?? [],
    currentUsername: currentPlayer.username,
    sendAction,
  });

  if (!gameState) {
    return (
      <div className="min-h-screen bg-[var(--win2k-felt-green)] flex items-center justify-center p-4">
        <Win2kWindow title={`Flip 7 - Room ${room.code}`} statusBar="Connecting...">
          <p className="text-[11px]">Waiting for game state...</p>
        </Win2kWindow>
      </div>
    );
  }
  const isFlipThree = gameState.turn_state === "resolving_flip_three";
  const isWaitingForTarget = gameState.turn_state === "waiting_for_target";
  const canAct =
    isMyTurn &&
    (gameState.turn_state === "waiting_for_decision" || isFlipThree);
  const myPlayerData = gameState.players.find(
    (p) => p.username === currentPlayer.username
  );
  const canStay =
    canAct &&
    !isFlipThree &&
    myPlayerData != null &&
    (myPlayerData.number_cards.length > 0 || myPlayerData.modifier_cards.length > 0);

  const myIndex = gameState.players.findIndex(
    (p) => p.username === currentPlayer.username
  );
  const otherPlayers = [
    ...gameState.players.slice(myIndex + 1),
    ...gameState.players.slice(0, myIndex),
  ];

  const highestTotal = Math.max(...Object.values(gameState.scores));
  const leaders = highestTotal > 0
    ? new Set(Object.entries(gameState.scores).filter(([, s]) => s === highestTotal).map(([u]) => u))
    : new Set<string>();

  const isSecondChancePending = gameState.pending_action?.subtype === "second_chance";

  const activeTargets = gameState.players
    .filter((p) => {
      if (p.status !== "active") return false;
      if (isSecondChancePending && p.action_cards.some((c) => c.subtype === "second_chance")) return false;
      return true;
    })
    .map((p) => ({
      username: p.username,
      roundScore: p.round_score,
      totalScore: gameState.scores[p.username] ?? 0,
    }))
    .sort((a, b) => {
      if (a.username === currentPlayer.username) return -1;
      if (b.username === currentPlayer.username) return 1;
      return 0;
    });

  const showTargetPicker =
    isMyTurn && isWaitingForTarget && gameState.pending_action != null;

  const currentTurnPlayer = gameState.players.find(
    (p) => p.username === gameState.current_player
  );
  const isCurrentTurnDisconnected = currentTurnPlayer != null && !currentTurnPlayer.connected;
  const canForceSkip =
    currentPlayer.is_admin &&
    !isMyTurn &&
    gameState.turn_state === "waiting_for_decision";
  const canForceAutoplay =
    currentPlayer.is_admin &&
    !isMyTurn &&
    currentTurnPlayer != null &&
    currentTurnPlayer.status === "active" &&
    !currentTurnPlayer.autoplay_mode;

  let statusText: string;
  if (isMyTurn && isWaitingForTarget) {
    const actionName = gameState.pending_action?.subtype ?? "action";
    statusText = `Select a target for ${actionName.replace("_", " ")}`;
  } else if (isMyTurn && isFlipThree) {
    statusText = `Flip 3! Must hit ${gameState.flip_three_remaining} more time${gameState.flip_three_remaining !== 1 ? "s" : ""}`;
  } else if (isMyTurn) {
    statusText = "Your turn — Hit or Stay";
  } else if (isWaitingForTarget) {
    statusText = `${gameState.current_player} is choosing a target...`;
  } else if (isFlipThree) {
    statusText = `${gameState.current_player} must hit ${gameState.flip_three_remaining} more time${gameState.flip_three_remaining !== 1 ? "s" : ""} (Flip 3)`;
  } else if (isCurrentTurnDisconnected) {
    statusText = `Waiting for ${gameState.current_player} (disconnected)...`;
  } else {
    statusText = `Waiting for ${gameState.current_player}...`;
  }

  const kickablePlayers = gameState.players
    .filter((p) => p.username !== currentPlayer.username)
    .map((p) => ({ username: p.username, connected: p.connected }));

  const myAutoplayMode = myAutoplayData?.autoplay_mode ?? null;

  const gameMenuBar = (
    <>
      {currentPlayer.is_admin && (
        <Win2kMenu label="Admin Tools">
          <Win2kMenuItem
            onClick={() => setShowForceSkipDialog(true)}
            disabled={!canForceSkip}
          >
            Skip Current Player...
          </Win2kMenuItem>
          <Win2kMenuItem onClick={() => setShowKickPicker(true)}>
            Kick Player...
          </Win2kMenuItem>
          <Win2kMenuItem
            onClick={() => sendAction("force_autoplay", { target: gameState.current_player, mode: "safe" })}
            disabled={!canForceAutoplay}
          >
            Autoplay Current Player (Safe)
          </Win2kMenuItem>
          <Win2kMenuItem
            onClick={() => sendAction("force_autoplay", { target: gameState.current_player, mode: "aggressive" })}
            disabled={!canForceAutoplay}
          >
            Autoplay Current Player (Aggressive)
          </Win2kMenuItem>
        </Win2kMenu>
      )}
      <Win2kMenu label="Help">
        <Win2kMenuItem onClick={() => setShowRules(true)}>
          How to Play
        </Win2kMenuItem>
        <Win2kMenuItem
          onClick={() => sendAction("set_autoplay", { mode: myAutoplayMode === "safe" ? null : "safe" })}
        >
          {myAutoplayMode === "safe" ? "✓ " : ""}Autoplay: Safe
        </Win2kMenuItem>
        <Win2kMenuItem
          onClick={() => sendAction("set_autoplay", { mode: myAutoplayMode === "aggressive" ? null : "aggressive" })}
        >
          {myAutoplayMode === "aggressive" ? "✓ " : ""}Autoplay: Aggressive
        </Win2kMenuItem>
      </Win2kMenu>
    </>
  );

  return (
    <DeckPositionProvider>
    <motion.div animate={shakeControls} className="min-h-screen bg-[var(--win2k-felt-dark)] flex flex-col">
      <Win2kWindow
        title={`Flip 7 - Room ${room.code}`}
        statusBar={`Round ${gameState.round} · ${statusText}`}
        menuBar={gameMenuBar}
        className="flex-1 max-w-none flex flex-col"
      >
        <div className="flex-1 flex flex-col lg:flex-row min-h-0">
          <FeltTable className="flex-1 flex flex-col gap-2 md:gap-3 overflow-y-auto">
            {myPlayerData && (
              <div className="space-y-2">
                <PlayerArea
                  player={myPlayerData}
                  isCurrentTurn={isMyTurn}
                  totalScore={gameState.scores[currentPlayer.username] ?? 0}
                  isLeader={leaders.has(currentPlayer.username)}
                />
                <GameControls
                  isMyTurn={canAct}
                  canStay={canStay}
                  flipThreeRemaining={isFlipThree && isMyTurn ? gameState.flip_three_remaining : 0}
                  onHit={() => sendAction("hit")}
                  onStay={() => sendAction("stay")}
                />
              </div>
            )}

            <div className="flex justify-center py-1">
              <DeckPile remaining={gameState.deck.remaining} />
            </div>

            <div className="space-y-1.5">
              {otherPlayers.map((player) => (
                <PlayerArea
                  key={player.username}
                  player={player}
                  isCurrentTurn={player.username === gameState.current_player}
                  totalScore={gameState.scores[player.username] ?? 0}
                  compact
                  isLeader={leaders.has(player.username)}
                  isTargetable={player.username === suspenseAttacker}
                  targetableCardType={pendingCardType}
                />
              ))}
            </div>
          </FeltTable>

          <div className="lg:w-48 lg:shrink-0 p-2">
            <Scoreboard
              players={gameState.players}
              scores={gameState.scores}
              currentTurnPlayer={gameState.current_player}
              myUsername={currentPlayer.username}
              scoreLimit={gameState.score_limit}
            />
          </div>
        </div>
      </Win2kWindow>

      {roundSummary && (
        <RoundEndSummary
          roundResults={roundSummary}
          onDismiss={() => {
            dismissedRoundRef.current = roundSummary.round;
            setRoundSummary(null);
            if (gameState?.finished) {
              setShowGameOver(true);
            }
          }}
        />
      )}

      {showGameOver && gameState && (
        <GameOverScreen
          winners={gameState.winners}
          scores={gameState.scores}
          playerOrder={gameState.player_order}
          myUsername={currentPlayer.username}
          onBackToLobby={() => sendAction("return_to_lobby")}
        />
      )}

      {showTargetPicker && gameState.pending_action && (
        <TargetPicker
          pendingAction={gameState.pending_action}
          targets={activeTargets}
          onSelectTarget={sendSelectTarget}
        />
      )}

      <ForceSkipDialog
        username={gameState.current_player}
        open={showForceSkipDialog}
        onConfirm={() => {
          setShowForceSkipDialog(false);
          sendAction("force_skip");
        }}
        onCancel={() => setShowForceSkipDialog(false)}
      />

      <KickPlayerPicker
        players={kickablePlayers}
        open={showKickPicker}
        onSelect={(username) => {
          setShowKickPicker(false);
          setKickTarget(username);
        }}
        onCancel={() => setShowKickPicker(false)}
      />

      <KickConfirmDialog
        username={kickTarget ?? ""}
        open={kickTarget !== null}
        onConfirm={handleKickConfirm}
        onCancel={() => setKickTarget(null)}
      />

      <RulesModal open={showRules} onClose={() => setShowRules(false)} />

      <AnimatePresence>
        {alertData && (
          <ActionCardAlert
            cardType={alertData.cardType}
            attackerName={alertData.attackerName}
            isSelfTargeted={alertData.isSelfTargeted}
            onDismiss={dismissAlert}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {flashColor && (
          <motion.div
            key="flash-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-30 pointer-events-none"
            style={{ backgroundColor: flashColor }}
          />
        )}
      </AnimatePresence>
    </motion.div>
    </DeckPositionProvider>
  );
}

function Lobby({
  room,
  initialPlayers,
  currentPlayer,
  onGameStart,
  onMessageRef,
  sendAction,
  onLeaveRoom,
}: {
  room: RoomShowProps["room"];
  initialPlayers: Player[];
  currentPlayer: RoomShowProps["current_player"];
  onGameStart: (gameState: GameStateData) => void;
  onMessageRef: React.MutableRefObject<(data: Record<string, unknown>) => void>;
  sendAction: (action: string, extra?: Record<string, unknown>) => void;
  onLeaveRoom: () => void;
}) {
  const [players, setPlayers] = useState<Player[]>(initialPlayers);
  const [showKickPicker, setShowKickPicker] = useState(false);
  const [kickTarget, setKickTarget] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);
  const onGameStartRef = useRef(onGameStart);
  onGameStartRef.current = onGameStart;
  const canStart = currentPlayer.is_admin && players.length >= 2;

  useEffect(() => {
    onMessageRef.current = (data: Record<string, unknown>) => {
      const type = data.type as string;
      console.log(`[GameChannel] Received message:`, data);

      if (type === "player_joined") {
        const player = data.player as Player;
        setPlayers((prev) => {
          if (prev.some((p) => p.username === player.username)) return prev;
          return [...prev, player];
        });
      } else if (type === "player_disconnected") {
        const username = data.username as string;
        setPlayers((prev) =>
          prev.map((p) =>
            p.username === username ? { ...p, connected: false } : p
          )
        );
      } else if (type === "player_reconnected") {
        const username = data.username as string;
        setPlayers((prev) =>
          prev.map((p) =>
            p.username === username ? { ...p, connected: true } : p
          )
        );
      } else if (type === "player_kicked" || type === "player_left") {
        const username = data.username as string;
        setPlayers((prev) => prev.filter((p) => p.username !== username));
      } else if (type === "game_state") {
        onGameStartRef.current(data.state as GameStateData);
      }
    };
  }, [onMessageRef]);

  function handleKickConfirm() {
    if (kickTarget) {
      sendAction("kick_player", { target: kickTarget });
    }
    setKickTarget(null);
  }

  function handleStartGame() {
    sendAction("start_game");
  }

  const kickablePlayers = players
    .filter((p) => !p.is_admin)
    .map((p) => ({ username: p.username, connected: p.connected }));

  const lobbyMenuBar = (
    <>
      <Win2kMenu label="Leave Room">
        <Win2kMenuItem onClick={onLeaveRoom}>Really leave room?</Win2kMenuItem>
      </Win2kMenu>
      {currentPlayer.is_admin && (
        <Win2kMenu label="Admin Tools">
          <Win2kMenuItem
            onClick={() => setShowKickPicker(true)}
            disabled={kickablePlayers.length === 0}
          >
            Kick Player...
          </Win2kMenuItem>
        </Win2kMenu>
      )}
      <button type="button" onClick={() => setShowRules(true)} className="px-1 cursor-default text-xs hover:underline">
        How to Play
      </button>
    </>
  );

  return (
    <div className="min-h-screen bg-[#008080] flex items-center justify-center p-4">
      <Win2kWindow
        title={`Flip 7 - Room ${room.code}`}
        statusBar={`${players.length} player${players.length !== 1 ? "s" : ""} in lobby`}
        menuBar={lobbyMenuBar}
        className="max-w-md"
        onClose={onLeaveRoom}
      >
        <div className="space-y-4">
          <div className="text-center">
            <p className="text-[11px] text-[var(--win2k-button-text)]">
              Room Code
            </p>
            <p className="font-pixel text-xl md:text-2xl tracking-widest text-[var(--win2k-black)] mt-1">
              {room.code}
            </p>
            <p className="text-[10px] text-[var(--win2k-shadow)] mt-1">
              Share this code with friends to join
            </p>
          </div>

          <fieldset className="win2k-inset p-2">
            <legend className="text-[11px] font-bold px-1">Players</legend>
            <div className="space-y-1">
              {players.map((player) => (
                <div
                  key={player.username}
                  className="flex items-center gap-2 px-1 py-0.5 text-[12px]"
                >
                  <span
                    className={`w-2 h-2 rounded-full shrink-0 ${
                      player.connected ? "bg-green-600" : "bg-yellow-600"
                    }`}
                    title={player.connected ? "Connected" : "Disconnected"}
                  />
                  <span className="flex-1 min-w-0 truncate">
                    {player.username}
                  </span>
                  {player.is_admin && (
                    <span className="text-[10px] px-1 bg-[var(--win2k-title-blue-start)] text-white shrink-0">
                      ADMIN
                    </span>
                  )}
                </div>
              ))}
            </div>
          </fieldset>

          {currentPlayer.is_admin && (
            <div className="space-y-1">
              <Win2kButton
                disabled={!canStart}
                className="w-full"
                onClick={handleStartGame}
              >
                Start Game
              </Win2kButton>
              {players.length < 2 && (
                <p className="text-[10px] text-center text-[var(--win2k-shadow)]">
                  Need at least 2 players to start
                </p>
              )}
            </div>
          )}

          {!currentPlayer.is_admin && (
            <p className="text-[11px] text-center text-[var(--win2k-shadow)]">
              Waiting for the host to start the game...
            </p>
          )}
        </div>
      </Win2kWindow>

      <KickPlayerPicker
        players={kickablePlayers}
        open={showKickPicker}
        onSelect={(username) => {
          setShowKickPicker(false);
          setKickTarget(username);
        }}
        onCancel={() => setShowKickPicker(false)}
      />

      <KickConfirmDialog
        username={kickTarget ?? ""}
        open={kickTarget !== null}
        onConfirm={handleKickConfirm}
        onCancel={() => setKickTarget(null)}
      />

      <RulesModal open={showRules} onClose={() => setShowRules(false)} />
    </div>
  );
}
